import { call, put, takeLatest, takeEvery, race, take, cancelled } from 'redux-saga/effects';
import { NONPAYMENT_DETAILS_DATA_REQUEST, NONPAYMENT_TOP_REASON_DATA_REQUEST, NONPAYMENT_CLAIMS_TREND_GRAPH_DATA_REQUEST, NONPAYMENT_COMPLETE_AND_TREND_DATA_REQUEST, NONPAYMENT_DETAILS_DATA_CANCEL, NONPAYMENT_TOP_REASON_DATA_CANCEL, NONPAYMENT_CLAIMS_TREND_GRAPH_DATA_CANCEL, NONPAYMENT_COMPLETE_AND_TREND_DATA_CANCEL, NONPAYMENT_SUBCATEGORY_REQUEST } from '../../../actions/actionTypes';
import { fetchNonPaymentDetailsSuccess, fetchNonPaymentDetailsError, fetchNonPaymentDetailsLoading,nonPaymentPollingStarted,nonPaymentPollingCompleted,nonPaymentPollingFailed, fetchNonPaymentTopReasonLoading, fetchNonPaymentTopReasonError, fetchNonPaymentTopReasonSuccess, fetchNonPaymentClaimsGraphLoading, fetchNonPaymentClaimsGraphSuccess, fetchNonPaymentClaimsGraphError, fetchNonPaymentClaimsLOBLoading, fetchNonPaymentClaimsLOBSuccess, fetchNonPaymentClaimsLOBError, fetchNonPaymentCompleteandPartialLoading, fetchNonPaymentCompleteandPartialSuccess, fetchNonPaymentCompleteandPartialError, fetchNonPaymentSubCategorySuccess, fetchNonPaymentSubCategoryFailure, fetchNonPaymentSubCategoryLoading } from '../../../actions/Reimbursement/nonPaymentActions';
import {getNonPaymentDetailsBenchMarkAvg, getNonPaymentDetails, getTopReasonTopCategory, getTopReasonSubCategory, getClaimsTrendGraph, getClaimsTrendLOB, getNonPaymentCompleteAndPartialTrend, getNonPaymentDetailsBulk, getTopReasonTopCategoryBulk, getTopReasonSubCategoryBulk, getClaimsTrendGraphBulk, getClaimsTrendLOBBulk, getNonPaymentCompleteAndPartialTrendBulk } from '../../../services/reimbusement/nonPayment/nonPayment.service';
import { checkSingleTin } from '@src/utility/createStorage';
import { reimbursementPollingStarted, reimbursementPollingCompleted } from '@src/middleware/actions/Reimbursement/reiumbursementTabDataAction';

const isNonPaymentBulkFlow = (): boolean => (
  sessionStorage.getItem('HIVOLUMETINFLOW') === 'true' ||
  sessionStorage.getItem('hiVolumeContractFlow') === 'true'
);

export function* fetchNonPaymentDetails(action: any) {
  const { payload } = action;
  const highVolumeTinFlag = isNonPaymentBulkFlow();
  const key = highVolumeTinFlag ? 'bulk-polling' : undefined;
  const isSingleTin = checkSingleTin();
  let stopPollingFn: (() => void) | undefined;

  const correlationId = `fetchNonPaymentDetails-${Date.now()}`;
  try {

    if (key) {
      yield put(nonPaymentPollingStarted());
      yield put(reimbursementPollingStarted(correlationId));
    }

    yield put(fetchNonPaymentDetailsLoading(true));

    // Race between API calls and cancellation
    const { result, cancel } = yield race({
      result: call(function* () {
        // Call the API services - use bulk versions if key is truthy
        const response = yield call(
          !key ? getNonPaymentDetails : getNonPaymentDetailsBulk,
          payload,
          {
            onStop: (stopFn: () => void) => {
              stopPollingFn = stopFn;
            },
          }
        );

        // Only call benchmark API if single TIN is selected
        let benchmarkResponse;
        if (isSingleTin && !key) {
          benchmarkResponse = yield call(getNonPaymentDetailsBenchMarkAvg,
            payload
          );
        }

        return { response, benchmarkResponse };
      }),
      cancel: take(NONPAYMENT_DETAILS_DATA_CANCEL),
    });

    if (cancel) {
      if (typeof stopPollingFn === 'function') {
        stopPollingFn();
      }
      yield put(fetchNonPaymentDetailsLoading(false));
      if (key) {
        yield put(nonPaymentPollingCompleted());
        yield put(reimbursementPollingCompleted(correlationId));
      }
      return;
    }

    const { response, benchmarkResponse } = result;

    // Reset loading state
    yield put(fetchNonPaymentDetailsLoading(false));
    // Check response status
    const responseValid = response && response?.Status === "OK";

    // Only validate benchmark if it was actually called (single TIN in non-bulk flow)
    // Benchmark is NOT called in bulk flow (when key exists) or when not single TIN
    const shouldHaveBenchmark = isSingleTin && !key;
    const benchmarkValid = !shouldHaveBenchmark || (benchmarkResponse && benchmarkResponse?.Status === "OK");

    if (!responseValid || !benchmarkValid) {
      // Handle error
      yield put(fetchNonPaymentDetailsError("Failed to fetch data"));
      if (key) {
        console.error('🔴 [fetchNonPaymentDetails] Error - decrementing counter');
        yield put(nonPaymentPollingFailed());
      }
    } else {
      // Handle success - combine both responses
      const combinedData = {
        details: response?.Data,
        benchmark: benchmarkResponse?.Data || undefined
      };
      yield put(fetchNonPaymentDetailsSuccess(combinedData));

      if (key) {
        yield put(nonPaymentPollingCompleted());
        yield put(reimbursementPollingCompleted(correlationId));
      }
    }
  } catch (error) {
    yield put(fetchNonPaymentDetailsLoading(false));
    yield put(fetchNonPaymentDetailsError("An error occurred"));

    if (key) {
      console.error('🔴 [fetchNonPaymentDetails] Error - decrementing counter');
      yield put(nonPaymentPollingFailed());
    }
  } finally {
    if (yield cancelled()) {
      if (typeof stopPollingFn === 'function') {
        stopPollingFn();
      }
      yield put(fetchNonPaymentDetailsLoading(false));
      if (key) {
        yield put(nonPaymentPollingCompleted());
        yield put(reimbursementPollingCompleted(correlationId));
      }
    }
  }
}

export function* fetchNonPaymentTopReasons(action: any) {
  const correlationId = `fetchNonPaymentTopReasons-${Date.now()}`;
  const highVolumeTinFlag = isNonPaymentBulkFlow();
  const key = highVolumeTinFlag ? 'bulk-polling' : undefined;
  let stopPollingFn: (() => void) | undefined;

  try {
    if (key) {
      yield put(nonPaymentPollingStarted());
      yield put(reimbursementPollingStarted(correlationId));
    }

    const ClaimsBy = action?.payload?.ClaimsBy;
    yield put(fetchNonPaymentTopReasonLoading(true));
    if (ClaimsBy === "DOP") {
      action.payload.ReportType = "subject";
    }

    // Race between API calls and cancellation
    const { result, cancel } = yield race({
      result: call(function* () {
        // First API call based on the initial payload - use bulk version if key is truthy
        const initialResponse = yield call(
          !key ? getTopReasonTopCategory : getTopReasonTopCategoryBulk,
          action?.payload,
          {
            onStop: (stopFn: () => void) => {
              stopPollingFn = stopFn;
            },
          }
        );

        if (initialResponse && (initialResponse.Status === "OK" || initialResponse.Status === "MULTI_STATUS")) {

          // Extract categories based on ClaimsBy value
          let denialCategories = [];

          if (ClaimsBy === "DOP") {
            // Try to safely extract the categories
            if (Array.isArray(initialResponse?.Data?.DenialCategory)) {
              denialCategories = initialResponse.Data.DenialCategory
                .map((detail: any) => detail.ClaimDenialCategoryLevel1ShortName)
                .filter(Boolean)
            }
          } else {
            // Non-DOP flow remains the same
            denialCategories = (initialResponse?.Data[0]?.All?.DenialCategory?.map((detail: any) =>
              detail.ClaimDenialCategoryLevel1ShortName) || []);
          }

          // subcategories response empty on demand
          return { initialResponse, allResponses: [] };
        } else {
          throw new Error("Invalid response from API");
        }
      }),
      cancel: take(NONPAYMENT_TOP_REASON_DATA_CANCEL),
    });

    if (cancel) {
      if (typeof stopPollingFn === 'function') {
        stopPollingFn();
      }
      yield put(fetchNonPaymentTopReasonLoading(false));
      if (key) {
        yield put(nonPaymentPollingCompleted());
        yield put(reimbursementPollingCompleted(correlationId));
      }
      return;
    }

    // Combine all responses and dispatch success action
    yield put(fetchNonPaymentTopReasonSuccess(result));
    yield put(fetchNonPaymentTopReasonLoading(false));

    if (key) {
      yield put(nonPaymentPollingCompleted());
      yield put(reimbursementPollingCompleted(correlationId));
    }
  } catch (error) {
    yield put(fetchNonPaymentTopReasonError(error));
    yield put(fetchNonPaymentTopReasonLoading(false));

    if (key) {
      yield put(nonPaymentPollingFailed());
    }
  } finally {
    if (yield cancelled()) {
      if (typeof stopPollingFn === 'function') {
        stopPollingFn();
      }
      yield put(fetchNonPaymentTopReasonLoading(false));
      if (key) {
        yield put(nonPaymentPollingCompleted());
        yield put(reimbursementPollingCompleted(correlationId));
      }
    }
  }
}

export function* fetchNonPaymentClaimsTrend(action: any) {
  const correlationId = `fetchNonPaymentClaimsTrend-${Date.now()}`;
  const highVolumeTinFlag = isNonPaymentBulkFlow();
  const key = highVolumeTinFlag ? 'bulk-polling' : undefined;
  let stopPollingFn: (() => void) | undefined;

  const LobData: { Lob: string }[] = [
    { Lob: "All" },
    { Lob: "EI" },
    { Lob: "MR" },
    { Lob: "CS" },
    { Lob: "EXC" }
  ];

  try {
    if (key) {
      yield put(nonPaymentPollingStarted());
      yield put(reimbursementPollingStarted(correlationId));
    }

    // Set loading states at the beginning
    yield put(fetchNonPaymentClaimsGraphLoading(true));
    yield put(fetchNonPaymentClaimsLOBLoading(true));

    // Race between API calls and cancellation
    const { result, cancel } = yield race({
      result: call(function* () {
        // Process getClaimsTrendGraph APIs (5 calls with different LOB values)
        const graphResponses: any[] = [];
        let graphErrors = false;

        for (const lobItem of LobData) {
          const correlationIdInner = `fetchNonPaymentClaimsTrend-Graph-${lobItem.Lob}-${Date.now()}`;
          try {
            if (key) {
              yield put(reimbursementPollingStarted(correlationIdInner));
            }
            // Call API with the original payload plus the specific LOB
            // For "All" scenario, don't send Lob in payload
            const payload = lobItem.Lob === "All"
              ? { ...action?.payload }
              : { ...action?.payload, ...lobItem };

            const response: any = yield call(
              !key ? getClaimsTrendGraph : getClaimsTrendGraphBulk,
              payload,
              {
                onStop: (stopFn: () => void) => {
                  stopPollingFn = stopFn;
                },
              }
            );

            if (!response || response?.Status !== "OK") {
              graphErrors = true;
            } else {
              // Add the LOB info to the response for identification
              graphResponses.push({
                lob: lobItem.Lob,
                data: response?.Data
              });
            }
          } finally {
            if (key) {
              yield put(reimbursementPollingCompleted(correlationIdInner));
            }
          }
        }

        // Process getClaimsTrendLOB APIs (5 calls with different LOB values)
        const lobResponses: any[] = [];
        let lobErrors = false;

        for (const lobItem of LobData) {
          const correlationIdInner = `fetchNonPaymentClaimsTrend-LOB-${lobItem.Lob}-${Date.now()}`;
          try {
            if (key) {
              yield put(reimbursementPollingStarted(correlationIdInner));
            }
            // Call API with the original payload plus the specific LOB
            // For "All" scenario, don't send Lob in payload
            const payload = lobItem.Lob === "All"
              ? { ...action?.payload }
              : { ...action?.payload, ...lobItem };

            const response: any = yield call(
              !key ? getClaimsTrendLOB : getClaimsTrendLOBBulk,
              payload,
              {
                onStop: (stopFn: () => void) => {
                  stopPollingFn = stopFn;
                },
              }
            );

            if (!response || response?.Status !== "OK") {
              lobErrors = true;
            } else {
              // Add the LOB info to the response for identification
              lobResponses.push({
                lob: lobItem.Lob,
                data: response?.Data
              });
            }
          } finally {
            if (key) {
              yield put(reimbursementPollingCompleted(correlationIdInner));
            }
          }
        }

        return { graphResponses, graphErrors, lobResponses, lobErrors };
      }),
      cancel: take(NONPAYMENT_CLAIMS_TREND_GRAPH_DATA_CANCEL),
    });

    if (cancel) {
      if (typeof stopPollingFn === 'function') {
        stopPollingFn();
      }
      yield put(fetchNonPaymentClaimsGraphLoading(false));
      yield put(fetchNonPaymentClaimsLOBLoading(false));
      if (key) {
        yield put(nonPaymentPollingCompleted());
        yield put(reimbursementPollingCompleted(correlationId));
      }
      return;
    }

    const { graphResponses, graphErrors, lobResponses, lobErrors } = result;

    // Set graph data results
    if (graphErrors) {
      yield put(fetchNonPaymentClaimsGraphError("Failed to fetch some graph data"));
    } else if (graphResponses.length > 0) {
      yield put(fetchNonPaymentClaimsGraphSuccess(graphResponses));
    }

    // Set LOB data results
    if (lobErrors) {
      yield put(fetchNonPaymentClaimsLOBError("Failed to fetch some LOB data"));
    } else if (lobResponses.length > 0) {
      yield put(fetchNonPaymentClaimsLOBSuccess(lobResponses));
    }

    // Reset loading states at the end
    yield put(fetchNonPaymentClaimsGraphLoading(false));
    yield put(fetchNonPaymentClaimsLOBLoading(false));

    if (key) {
      yield put(nonPaymentPollingCompleted());
      yield put(reimbursementPollingCompleted(correlationId));
    }
  } catch (error) {
    // Handle any errors that occurred during the process
    yield put(fetchNonPaymentClaimsGraphLoading(false));
    yield put(fetchNonPaymentClaimsLOBLoading(false));
    yield put(fetchNonPaymentClaimsGraphError("An error occurred while fetching graph data"));
    yield put(fetchNonPaymentClaimsLOBError("An error occurred while fetching LOB data"));

    if (key) {
      yield put(nonPaymentPollingFailed());
    }
  } finally {
    if (yield cancelled()) {
      if (typeof stopPollingFn === 'function') {
        stopPollingFn();
      }
      yield put(fetchNonPaymentClaimsGraphLoading(false));
      yield put(fetchNonPaymentClaimsLOBLoading(false));
      if (key) {
        yield put(nonPaymentPollingCompleted());
        yield put(reimbursementPollingCompleted(correlationId));
      }
    }
  }
}

export function* fetchNonPaymentCompleteAndPartialTrend(action: any) {
  const highVolumeTinFlag = isNonPaymentBulkFlow();
  const key = highVolumeTinFlag ? 'bulk-polling' : undefined;
  let stopPollingFn: (() => void) | undefined;
  const correlationId = `fetchNonPaymentCompleteAndPartialTrend-${Date.now()}`;
  try {
    if (key) {
      yield put(nonPaymentPollingStarted());
      yield put(reimbursementPollingStarted(correlationId));
    }

    yield put(fetchNonPaymentCompleteandPartialLoading(true));

    // Race between API calls and cancellation
    const { result, cancel } = yield race({
      result: call(
        !key ? getNonPaymentCompleteAndPartialTrend : getNonPaymentCompleteAndPartialTrendBulk,
        action?.payload,
        {
          onStop: (stopFn: () => void) => {
            stopPollingFn = stopFn;
          },
        }
      ),
      cancel: take(NONPAYMENT_COMPLETE_AND_TREND_DATA_CANCEL),
    });

    if (cancel) {
      if (typeof stopPollingFn === 'function') {
        stopPollingFn();
      }
      yield put(fetchNonPaymentCompleteandPartialLoading(false));
      if (key) {
        yield put(nonPaymentPollingCompleted());
        yield put(reimbursementPollingCompleted(correlationId));
      }
      return;
    }

    // Reset loading state
    yield put(fetchNonPaymentCompleteandPartialLoading(false));

    if (!result || result?.Status !== "OK") {
      // Handle error
      yield put(fetchNonPaymentCompleteandPartialError("Failed to fetch data"));
      if (key) {
        console.error('🔴 [fetchNonPaymentCompleteAndPartialTrend] Error - decrementing counter');
        yield put(nonPaymentPollingFailed());
      }
    } else {
      // Handle success
      yield put(fetchNonPaymentCompleteandPartialSuccess(result?.Data));

      if (key) {
        yield put(nonPaymentPollingCompleted());
        yield put(reimbursementPollingCompleted(correlationId));
      }
    }
  } catch (error) {
    yield put(fetchNonPaymentCompleteandPartialLoading(false));
    yield put(fetchNonPaymentCompleteandPartialError("An error occurred"));

    if (key) {
      console.error('🔴 [fetchNonPaymentCompleteAndPartialTrend] Error - decrementing counter');
      yield put(nonPaymentPollingFailed());
    }
  } finally {
    if (yield cancelled()) {
      if (typeof stopPollingFn === 'function') {
        stopPollingFn();
      }
      yield put(fetchNonPaymentCompleteandPartialLoading(false));
      if (key) {
        yield put(nonPaymentPollingCompleted());
        yield put(reimbursementPollingCompleted(correlationId));
      }
    }
  }
}

export function* fetchNonPaymentSubCategory(action: any) {
  const { categoryName, apiPayload } = action.payload;
  const highVolumeTinFlag = isNonPaymentBulkFlow();
  const key = highVolumeTinFlag ? 'bulk-polling' : undefined;

  try {
    yield put(fetchNonPaymentSubCategoryLoading({ categoryName, loading: true }));

    const response: any = yield call(
      !key ? getTopReasonSubCategory : getTopReasonSubCategoryBulk,
      apiPayload
    );

    if (response && (response.Status === 'OK' || response.Status === 'MULTI_STATUS')) {
      yield put(fetchNonPaymentSubCategorySuccess({ categoryName, data: response }));
    } else {
      yield put(fetchNonPaymentSubCategoryFailure({ categoryName, error: 'Invalid response' }));
    }
  } catch (error) {
    yield put(fetchNonPaymentSubCategoryFailure({ categoryName, error }));
  } finally {
    yield put(fetchNonPaymentSubCategoryLoading({ categoryName, loading: false }));
  }
}

export function* nonPaymentSaga() {
  yield takeLatest(NONPAYMENT_DETAILS_DATA_REQUEST, fetchNonPaymentDetails);
  yield takeLatest(NONPAYMENT_TOP_REASON_DATA_REQUEST, fetchNonPaymentTopReasons);
  yield takeLatest(NONPAYMENT_CLAIMS_TREND_GRAPH_DATA_REQUEST, fetchNonPaymentClaimsTrend);
  yield takeLatest(NONPAYMENT_COMPLETE_AND_TREND_DATA_REQUEST, fetchNonPaymentCompleteAndPartialTrend);
  // Use takeEvery so multiple categories can be fetched concurrently
  yield takeEvery(NONPAYMENT_SUBCATEGORY_REQUEST, fetchNonPaymentSubCategory);
}

export default [nonPaymentSaga];
